export const CLICKS_PER_POINT = 5;
export const MIN_CALIBRATION_ACCURACY = 75;
export const ACCURACY_STARE_MS = 5000;

export type CalibrationPointId =
  | 'Pt1'
  | 'Pt2'
  | 'Pt3'
  | 'Pt4'
  | 'Pt5'
  | 'Pt6'
  | 'Pt7'
  | 'Pt8'
  | 'Pt9';

export const CALIBRATION_POINTS: {
  id: CalibrationPointId;
  xPercent: number;
  yPercent: number;
  isCenter: boolean;
}[] = [
  { id: 'Pt1', xPercent: 10, yPercent: 10, isCenter: false },
  { id: 'Pt2', xPercent: 50, yPercent: 10, isCenter: false },
  { id: 'Pt3', xPercent: 90, yPercent: 10, isCenter: false },
  { id: 'Pt4', xPercent: 10, yPercent: 50, isCenter: false },
  { id: 'Pt5', xPercent: 50, yPercent: 50, isCenter: true },
  { id: 'Pt6', xPercent: 90, yPercent: 50, isCenter: false },
  { id: 'Pt7', xPercent: 10, yPercent: 90, isCenter: false },
  { id: 'Pt8', xPercent: 50, yPercent: 90, isCenter: false },
  { id: 'Pt9', xPercent: 90, yPercent: 90, isCenter: false },
];

export type CalibrationPhase = 'ready' | 'points' | 'accuracy' | 'result';

const OUTER_POINT_IDS: CalibrationPointId[] = [
  'Pt1',
  'Pt2',
  'Pt3',
  'Pt4',
  'Pt6',
  'Pt7',
  'Pt8',
  'Pt9',
];

const ALL_POINT_IDS: CalibrationPointId[] = [
  'Pt1',
  'Pt2',
  'Pt3',
  'Pt4',
  'Pt5',
  'Pt6',
  'Pt7',
  'Pt8',
  'Pt9',
];

function emptyClickRecord(): Record<CalibrationPointId, number> {
  return Object.fromEntries(ALL_POINT_IDS.map((id) => [id, 0])) as Record<
    CalibrationPointId,
    number
  >;
}

function emptyCompletedRecord(): Record<CalibrationPointId, boolean> {
  return Object.fromEntries(ALL_POINT_IDS.map((id) => [id, false])) as Record<
    CalibrationPointId,
    boolean
  >;
}

export interface CalibrationUiState {
  phase: CalibrationPhase;
  clicks: Record<CalibrationPointId, number>;
  completed: Record<CalibrationPointId, boolean>;
  completedCount: number;
  accuracy?: number;
}

export function createInitialCalibrationState(): CalibrationUiState {
  return {
    phase: 'ready',
    clicks: emptyClickRecord(),
    completed: emptyCompletedRecord(),
    completedCount: 0,
  };
}

export function pointOpacity(clicks: number): number {
  return 0.2 * clicks + 0.2;
}

function countOuterCompleted(
  completed: Record<CalibrationPointId, boolean>
): number {
  return OUTER_POINT_IDS.filter((id) => completed[id]).length;
}

export function isCenterVisible(state: CalibrationUiState): boolean {
  return countOuterCompleted(state.completed) >= 8;
}

export function shouldStartAccuracy(state: CalibrationUiState): boolean {
  return state.completedCount >= 9;
}

export function canStartExam(accuracy: number): boolean {
  return accuracy >= MIN_CALIBRATION_ACCURACY;
}

export function recordPointClick(
  state: CalibrationUiState,
  pointId: CalibrationPointId
): CalibrationUiState {
  if (state.completed[pointId]) {
    return state;
  }

  if (pointId === 'Pt5' && !isCenterVisible(state)) {
    return state;
  }

  const nextClicks = state.clicks[pointId] + 1;
  const clicks = { ...state.clicks, [pointId]: nextClicks };

  if (nextClicks < CLICKS_PER_POINT) {
    return {
      ...state,
      phase: state.phase === 'ready' ? 'points' : state.phase,
      clicks,
    };
  }

  const completed = { ...state.completed, [pointId]: true };
  return {
    ...state,
    phase: state.phase === 'ready' ? 'points' : state.phase,
    clicks,
    completed,
    completedCount: state.completedCount + 1,
  };
}

export function applyAccuracyResult(
  state: CalibrationUiState,
  accuracy: number
): CalibrationUiState {
  return {
    ...state,
    phase: 'result',
    accuracy,
  };
}
